import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faEye, faEyeSlash, faLock } from '@fortawesome/free-solid-svg-icons';
import { adminService } from '../../services/adminService';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody,
  FormGroup,
  FormLabel,
  FormInput,
  PasswordInputWrapper,
  PasswordToggle,
  ErrorText,
  ModalFooter,
  SubmitButton,
  CancelButton
} from './ModalStyles';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

export function ChangePasswordModal({ isOpen, onClose, onSuccess, onError }: ChangePasswordModalProps) {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    if (errors[name] || errors.general) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        delete newErrors.general; // Clear general error when user starts typing
        return newErrors;
      });
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.currentPassword) {
      newErrors.currentPassword = 'Le mot de passe actuel est requis';
    }

    if (!formData.newPassword) {
      newErrors.newPassword = 'Le nouveau mot de passe est requis';
    } else if (formData.newPassword.length < 8) {
      newErrors.newPassword = 'Le mot de passe doit contenir au moins 8 caractères';
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'La confirmation du mot de passe est requise';
    } else if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    }

    if (formData.currentPassword === formData.newPassword) {
      newErrors.newPassword = 'Le nouveau mot de passe doit être différent de l\'actuel';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      await adminService.changePassword(
        formData.currentPassword,
        formData.newPassword,
        formData.confirmPassword
      );
      onSuccess('Mot de passe modifié avec succès');
      handleClose();
    } catch (error: any) {
      if (error.response?.data?.errors) {
        // Erreurs de validation spécifiques aux champs
        const fieldErrors: {[key: string]: string} = {};
        const serverErrors = error.response.data.errors;
        
        if (serverErrors.current_password) {
          fieldErrors.currentPassword = Array.isArray(serverErrors.current_password) 
            ? serverErrors.current_password[0] 
            : serverErrors.current_password;
        }
        if (serverErrors.new_password) {
          fieldErrors.newPassword = Array.isArray(serverErrors.new_password) 
            ? serverErrors.new_password[0] 
            : serverErrors.new_password;
        }
        if (serverErrors.new_password_confirmation) {
          fieldErrors.confirmPassword = Array.isArray(serverErrors.new_password_confirmation) 
            ? serverErrors.new_password_confirmation[0] 
            : serverErrors.new_password_confirmation;
        }
        
        setErrors(fieldErrors);
      } else if (error.response?.data?.message) {
        // Message d'erreur général (comme "mot de passe actuel incorrect")
        if (error.response.data.message.includes('mot de passe actuel')) {
          setErrors({ currentPassword: error.response.data.message });
        } else {
          setErrors({ general: error.response.data.message });
        }
      } else {
        setErrors({ general: 'Erreur lors du changement de mot de passe' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setErrors({});
    setShowPasswords({
      current: false,
      new: false,
      confirm: false
    });
    setIsLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={handleClose}>
      <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <FontAwesomeIcon icon={faLock} />
            Changer le mot de passe
          </ModalTitle>
          <CloseButton onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </ModalHeader>

        <form onSubmit={handleSubmit}>
          <ModalBody>
            {errors.general && (
              <div style={{ 
                background: '#fef2f2', 
                border: '1px solid #fecaca', 
                borderRadius: '8px', 
                padding: '0.75rem', 
                marginBottom: '1rem',
                color: '#dc2626'
              }}>
                {errors.general}
              </div>
            )}
            
            <FormGroup>
              <FormLabel htmlFor="currentPassword">Mot de passe actuel</FormLabel>
              <PasswordInputWrapper>
                <FormInput
                  type={showPasswords.current ? 'text' : 'password'}
                  id="currentPassword"
                  name="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  placeholder="Entrez votre mot de passe actuel"
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => togglePasswordVisibility('current')}
                >
                  <FontAwesomeIcon icon={showPasswords.current ? faEyeSlash : faEye} />
                </PasswordToggle>
              </PasswordInputWrapper>
              {errors.currentPassword && <ErrorText>{errors.currentPassword}</ErrorText>}
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="newPassword">Nouveau mot de passe</FormLabel>
              <PasswordInputWrapper>
                <FormInput
                  type={showPasswords.new ? 'text' : 'password'}
                  id="newPassword"
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleInputChange}
                  placeholder="Entrez votre nouveau mot de passe"
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => togglePasswordVisibility('new')}
                >
                  <FontAwesomeIcon icon={showPasswords.new ? faEyeSlash : faEye} />
                </PasswordToggle>
              </PasswordInputWrapper>
              {errors.newPassword && <ErrorText>{errors.newPassword}</ErrorText>}
            </FormGroup>

            <FormGroup>
              <FormLabel htmlFor="confirmPassword">Confirmer le nouveau mot de passe</FormLabel>
              <PasswordInputWrapper>
                <FormInput
                  type={showPasswords.confirm ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  placeholder="Confirmez votre nouveau mot de passe"
                  required
                />
                <PasswordToggle
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm')}
                >
                  <FontAwesomeIcon icon={showPasswords.confirm ? faEyeSlash : faEye} />
                </PasswordToggle>
              </PasswordInputWrapper>
              {errors.confirmPassword && <ErrorText>{errors.confirmPassword}</ErrorText>}
            </FormGroup>
          </ModalBody>

          <ModalFooter>
            <CancelButton type="button" onClick={handleClose}>
              Annuler
            </CancelButton>
            <SubmitButton type="submit" disabled={isLoading}>
              {isLoading ? 'Changement en cours...' : 'Changer le mot de passe'}
            </SubmitButton>
          </ModalFooter>
        </form>
      </ModalContent>
    </ModalOverlay>
  );
}
